/**
 * Nutrition basis normalization (spec section 8).
 *
 * The single job here: given whatever bases a product declares, produce a
 * per-100g (solids) or per-100ml (liquids) view *when that is arithmetically
 * justified*, and refuse otherwise. Every refusal is reported with a reason so
 * the UI can render "Insufficient data" and say why.
 */

import type {
  NutritionBasis,
  NutritionFacts,
  Nutrients,
  Product,

  QuantityKind,
} from '@/types/product';

/** The empty nutrient set — all unknown, never zero. */
export function emptyNutrients(): Nutrients {
  return {
    kcal: null,
    protein: null,
    carbohydrate: null,
    fat: null,
    fibre: null,
    sugar: null,
    saturatedFat: null,
    sodium: null,
    other: {},
  };
}

/** Scale every known nutrient by `factor`. Unknowns stay unknown. */
export function scaleNutrients(n: Nutrients, factor: number): Nutrients {
  const s = (v: number | null): number | null =>
    v === null || !Number.isFinite(v) ? null : v * factor;
  const other: Nutrients['other'] = {};
  for (const [key, entry] of Object.entries(n.other)) {
    other[key] = { value: entry.value * factor, unit: entry.unit };
  }
  return {
    kcal: s(n.kcal),
    protein: s(n.protein),
    carbohydrate: s(n.carbohydrate),
    fat: s(n.fat),
    fibre: s(n.fibre),
    sugar: s(n.sugar),
    saturatedFat: s(n.saturatedFat),
    sodium: s(n.sodium),
    other,
  };
}

/** True when at least one modelled nutrient is present. */
export function hasAnyNutrient(n: Nutrients): boolean {
  return (
    n.kcal !== null ||
    n.protein !== null ||
    n.carbohydrate !== null ||
    n.fat !== null ||
    n.fibre !== null ||
    n.sugar !== null ||
    n.saturatedFat !== null ||
    n.sodium !== null ||
    Object.keys(n.other).length > 0
  );
}

/** The per-100 basis appropriate to a package's physical kind. */
export function per100BasisFor(kind: QuantityKind): NutritionBasis | null {
  if (kind === 'mass') return 'per_100g';
  if (kind === 'volume') return 'per_100ml';
  // A pure count package (e.g. "6 eggs") has no mass/volume denominator, so
  // there is no meaningful per-100 basis.
  return null;
}

export interface NormalizationResult {
  /** The per-100g / per-100ml view, or `null` when not derivable. */
  facts: NutritionFacts | null;
  /** Why it could not be derived — surfaced verbatim in the UI. */
  reason: string | null;
}

/**
 * Derive the per-100g / per-100ml view for a product.
 *
 * Preference order:
 *   1. An explicitly declared per-100 basis (no arithmetic, no error).
 *   2. Any other basis that can be tied to a physical amount.
 *
 * Cross-kind conversion is never attempted: per-100g facts on a liquid product
 * are reported as-is rather than converted through an assumed density.
 */
export function normalizeToPer100(product: Product): NormalizationResult {
  const quantity = product.quantity;
  const declaredBases = Object.values(product.nutrition).filter(
    (f): f is NutritionFacts => f != null,
  );

  if (declaredBases.length === 0) {
    return { facts: null, reason: 'No nutrition information available for this product.' };
  }

  // 1. Already declared per-100 — use it directly.
  const direct = product.nutrition.per_100g ?? product.nutrition.per_100ml ?? null;
  if (direct != null && hasAnyNutrient(direct.nutrients)) {
    return { facts: direct, reason: null };
  }

  if (quantity == null) {
    return {
      facts: null,
      reason:
        'Package size is unknown, so per-serving values cannot be converted to a per-100 basis.',
    };
  }

  const target = per100BasisFor(quantity.kind);
  if (target === null) {
    return {
      facts: null,
      reason:
        'This product is sold by piece count only, so there is no weight or volume to normalize against.',
    };
  }

  // 2. No conversion.
  //
  // Rescaling a per-serving panel into per-100 g produces numbers that are not
  // on the label. Manufacturers round each declared figure independently, so
  // "8 g protein per 40 g serving" does not reliably mean "20 g per 100 g" —
  // and the result would be displayed as though the manufacturer stated it.
  //
  // The per-100 view is therefore only ever a basis the label itself declares.
  // Anything else is reported as unavailable, with the basis we DO hold named
  // so the user can read the real panel instead.
  const held = declaredBases.map((f) => labelForBasis(f.basis)).join(', ');
  void target;
  return {
    facts: null,
    reason: `This product's nutrition is declared ${held}, not per 100 ${
      quantity.kind === 'volume' ? 'ml' : 'g'
    }. Converting it would produce figures the manufacturer did not state, so per-100 values are not shown.`,
  };
}

/**
 * Nutrients for the whole package. Needed for "protein per dollar", which is a
 * property of what you actually buy, not of an abstract 100 g.
 */
export function normalizeToPackage(product: Product): NormalizationResult {
  const quantity = product.quantity;

  const declared = product.nutrition.per_package;
  if (declared != null && hasAnyNutrient(declared.nutrients)) {
    return { facts: declared, reason: null };
  }

  if (quantity == null) {
    return { facts: null, reason: 'Package size is unknown.' };
  }

  // Per-100 route: needs a mass/volume package size.
  const per100 = normalizeToPer100(product);
  if (per100.facts == null) return { facts: null, reason: per100.reason };

  if (quantity.kind === 'count' || quantity.netAmount <= 0) {
    return {
      facts: null,
      reason: 'Package weight or volume is unknown, so package totals cannot be computed.',
    };
  }

  const factor = quantity.netAmount / 100;
  return {
    facts: {
      basis: 'per_package',
      basisAmount: quantity.netAmount,
      nutrients: scaleNutrients(per100.facts.nutrients, factor),
      source: {
        ...per100.facts.source,
        derived: true,
        note: `Scaled from ${labelForBasis(per100.facts.basis)} x ${quantity.netAmount / 100}.`,
      },
    },
    reason: null,
  };
}

export function labelForBasis(basis: NutritionBasis): string {
  switch (basis) {
    case 'per_100g':
      return 'per 100 g';
    case 'per_100ml':
      return 'per 100 ml';
    case 'per_serving':
      return 'per serving';
    case 'per_package':
      return 'per package';
    case 'per_piece':
      return 'per piece';
    default:
      return basis;
  }
}
